// ─────────────────────────────────────────────────────────────────────────────
// Every language a class can teach, or its students can speak.
//
// A class has two codes (classrooms.language / native_language). Everything
// language-specific on the site and in the extension reads from here, so
// adding a language is one entry.
//
//   google   the code Google Cloud Translation and Supadata understand
//   speech   the browser read-aloud voice to ask for
//   dir      rtl for Arabic — text containers also carry dir="auto"
//   keys     the special letters the grammar typing pad offers
//   hello    the greeting on the student home (in the language being LEARNED)
//   praise   lesson feedback, in the language being learned
//
// The Gibberfish app's translation cache is keyed by DISPLAY NAMES ('Spanish'
// as the learned language, 'Español' as a native language), so the
// classroom-translate function keeps its own copy of those two mappings —
// see APP_TARGET / APP_NATIVE there. Keep the codes in step.
// ─────────────────────────────────────────────────────────────────────────────

export type Lang = {
  name: string;
  /** The language's name for itself. */
  native: string;
  google: string;
  speech: string;
  dir: 'ltr' | 'rtl';
  keys: string[];
  hello: string;
  praise: string[];
};

export const LANGS: Record<string, Lang> = {
  es: {
    name: 'Spanish', native: 'Español', google: 'es', speech: 'es-ES', dir: 'ltr',
    keys: ['á', 'é', 'í', 'ó', 'ú', 'ñ', 'ü', '¿', '¡'], hello: '¡Hola',
    praise: ['¡Excelente!', '¡Muy bien!', '¡Perfecto!', '¡Genial!', '¡Bien hecho!', '¡Fantástico!'],
  },
  en: {
    name: 'English', native: 'English', google: 'en', speech: 'en-US', dir: 'ltr',
    keys: [], hello: 'Hi',
    praise: ['Excellent!', 'Great job!', 'Perfect!', 'Awesome!', 'Well done!', 'Fantastic!'],
  },
  fr: {
    name: 'French', native: 'Français', google: 'fr', speech: 'fr-FR', dir: 'ltr',
    keys: ['à', 'â', 'ç', 'é', 'è', 'ê', 'ë', 'î', 'ï', 'ô', 'ù', 'û', 'œ'], hello: 'Bonjour',
    praise: ['Excellent !', 'Très bien !', 'Parfait !', 'Génial !', 'Bravo !', 'Super !'],
  },
  de: {
    name: 'German', native: 'Deutsch', google: 'de', speech: 'de-DE', dir: 'ltr',
    keys: ['ä', 'ö', 'ü', 'ß', 'Ä', 'Ö', 'Ü'], hello: 'Hallo',
    praise: ['Ausgezeichnet!', 'Sehr gut!', 'Perfekt!', 'Toll!', 'Gut gemacht!', 'Super!'],
  },
  it: {
    name: 'Italian', native: 'Italiano', google: 'it', speech: 'it-IT', dir: 'ltr',
    keys: ['à', 'è', 'é', 'ì', 'ò', 'ù'], hello: 'Ciao',
    praise: ['Eccellente!', 'Molto bene!', 'Perfetto!', 'Fantastico!', 'Bravo!', 'Ottimo!'],
  },
  pt: {
    name: 'Portuguese', native: 'Português', google: 'pt', speech: 'pt-BR', dir: 'ltr',
    keys: ['á', 'â', 'ã', 'à', 'ç', 'é', 'ê', 'í', 'ó', 'ô', 'õ', 'ú'], hello: 'Olá',
    praise: ['Excelente!', 'Muito bem!', 'Perfeito!', 'Ótimo!', 'Mandou bem!', 'Fantástico!'],
  },
  'ar-EG': {
    name: 'Egyptian Arabic', native: 'مصري', google: 'ar', speech: 'ar-EG', dir: 'rtl',
    keys: [], hello: 'أهلاً',
    praise: ['ممتاز!', 'برافو!', 'تمام!', 'جميل!', 'شاطر!', 'عظيم!'],
  },
  ar: {
    name: 'Modern Standard Arabic (MSA)', native: 'الفصحى', google: 'ar', speech: 'ar-SA', dir: 'rtl',
    keys: [], hello: 'مرحباً',
    praise: ['ممتاز!', 'أحسنت!', 'رائع!', 'جيد جداً!', 'عمل رائع!', 'مذهل!'],
  },
};

/** Codes in the order the class form lists them. */
export const LANG_CODES = ['es', 'en', 'fr', 'de', 'it', 'pt', 'ar-EG', 'ar'];

const FALLBACK: Lang = LANGS.en;

export const lang = (code: string | null | undefined): Lang => (code && LANGS[code]) || FALLBACK;
export const languageName = (code: string | null | undefined) => (code && LANGS[code]?.name) || code || '';
export const speechCode = (code: string) => lang(code).speech;
export const isRtl = (code: string | null | undefined) => lang(code).dir === 'rtl';
